import type { ReactNode } from 'react';
import { Box, Typography } from '@mui/material';

import { COLORS } from '../../IndividualPlan/tokens';
import { REPORTS } from '../tokens';

export const OUTLINED_ACTION_SX = {
  height: 32,
  padding: '0 16px',
  bgcolor: REPORTS.cardBg,
  border: `1px solid ${COLORS.divider}`,
  color: REPORTS.ink,
  '&:hover': { bgcolor: 'rgba(8, 8, 27, 0.04)' },
} as const;

const BUTTON_SX = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '8px',
  height: 32,
  borderRadius: '8px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 16,
  fontWeight: 600,
  lineHeight: '20px',
  letterSpacing: '0.0313em',
  whiteSpace: 'nowrap',
  '&:disabled': { cursor: 'default' },
} as const;

const VARIANT_SX = {
  contained: {
    padding: '0 16px',
    border: 0,
    bgcolor: COLORS.navy,
    color: '#FFFFFF',
    '&:hover': { bgcolor: COLORS.indigo },
  },
  outlined: OUTLINED_ACTION_SX,
  text: {
    padding: '0 8px',
    border: 0,
    bgcolor: 'transparent',
    color: REPORTS.action,
    '&:hover': { bgcolor: REPORTS.welcome.bg },
  },
} as const;

export type ReportCardButtonVariant = keyof typeof VARIANT_SX;

// The only button a report card footer draws, so every card's actions share
// one size and three looks. There is no default look: each action names one.
export function ReportCardButton({
  label,
  name,
  variant,
  disabled = false,
  onClick,
}: {
  label: string;
  name?: string;
  variant: ReportCardButtonVariant;
  disabled?: boolean;
  onClick?: () => void;
}) {
  return (
    <Box
      component="button"
      type="button"
      aria-label={name}
      disabled={disabled}
      onClick={onClick}
      sx={{ ...BUTTON_SX, ...VARIANT_SX[variant] }}
    >
      {label}
    </Box>
  );
}

export function ActionFooter({
  lastGenerated,
  actionLabel,
  actionName,
  onAction,
  variant,
}: {
  lastGenerated: string | null;
  actionLabel: string;
  actionName?: string;
  onAction: () => void;
  variant: ReportCardButtonVariant;
}) {
  return (
    <Box
      sx={{
        mt: 'auto',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <Typography
        data-testid="last-generated"
        aria-hidden={lastGenerated === null || undefined}
        sx={{
          fontSize: 12,
          fontWeight: 500,
          lineHeight: '16px',
          color: REPORTS.muted,
          opacity: lastGenerated === null ? 0 : 1,
        }}
      >
        {lastGenerated === null ? 'Last generated:' : `Last generated: ${lastGenerated}`}
      </Typography>
      <ReportCardButton label={actionLabel} name={actionName} variant={variant} onClick={onAction} />
    </Box>
  );
}

export function PendingFooter() {
  return (
    <Box
      sx={{
        mt: 'auto',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        height: 36,
      }}
    >
      <Box
        data-testid="action-footer-pending"
        sx={{
          width: 105,
          height: 36,
          borderRadius: '8px',
          bgcolor: REPORTS.chip.bg,
        }}
      />
    </Box>
  );
}

export function ReportCard({
  title,
  description,
  coverSrc,
  footer,
}: {
  title: string;
  description: string;
  coverSrc?: string;
  footer: ReactNode;
}) {
  return (
    <Box
      data-testid="report-card"
      sx={{
        minWidth: 0,
        display: 'flex',
        flexDirection: 'row',
        padding: 0,
        overflow: 'hidden',
        bgcolor: REPORTS.cardBg,
        borderRadius: '12px',
        boxShadow: REPORTS.cardShadow,
      }}
    >
      <Box
        data-testid="report-body"
        sx={{
          flex: '1 1 0%',
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          gap: '16px',
          padding: '20px 24px 24px',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography
            data-testid="report-title"
            sx={{
              fontSize: 20,
              fontWeight: 700,
              lineHeight: '28px',
              color: REPORTS.ink,
              textTransform: 'none',
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{ fontSize: 16, fontWeight: 500, lineHeight: '24px', color: REPORTS.muted }}
          >
            {description}
          </Typography>
        </Box>
        {footer}
      </Box>
      {coverSrc !== undefined && (
        <Box
          data-testid="report-cover"
          sx={{
            flex: '1 1 0%',
            minWidth: 0,
            position: 'relative',
            overflow: 'hidden',
            bgcolor: REPORTS.cover.bg,
          }}
        >
          <Box
            component="img"
            src={coverSrc}
            alt=""
            role="presentation"
            sx={{
              position: 'absolute',
              top: 32,
              left: 32,
              width: 400,
              height: 'auto',
              display: 'block',
            }}
          />
        </Box>
      )}
    </Box>
  );
}
